import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { cn } from '../lib/cn'
import { AnimatedNumber } from './AnimatedNumber'

interface StatCardProps {
  label: string
  value: number
  suffix?: string | undefined
  /** For money. `AnimatedNumber` groups the digits; this puts the symbol on. */
  prefix?: string | undefined
  change?: number | undefined
  changeLabel?: string | undefined
  icon: LucideIcon
  accent?: string | undefined
  index?: number | undefined
}

export function StatCard({ label, value, suffix, prefix, change, changeLabel = 'vs last month', icon: Icon, accent = '#FC2265', index = 0 }: StatCardProps) {
  const positive = (change ?? 0) >= 0
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '0px 0px -40px 0px' }}
      transition={{ type: 'spring', stiffness: 300, damping: 26, mass: 0.9, delay: index * 0.06 }}
      whileHover={{ y: -4, scale: 1.02, transition: { type: 'spring', stiffness: 420, damping: 22, mass: 0.7 } }}
      whileTap={{ scale: 0.97, transition: { type: 'spring', stiffness: 500, damping: 24 } }}
      className="group glass-panel rounded-card p-5"
    >
      <div className="flex items-start justify-between">
        <p className="text-sm font-semibold text-canvas-fg/75">{label}</p>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-110"
          style={{ backgroundColor: `${accent}33`, color: accent }}
        >
          <Icon size={17} strokeWidth={2.2} />
        </div>
      </div>
      <p className="mt-3 font-display text-3xl font-bold tracking-tight text-canvas-fg">
        <AnimatedNumber value={value} suffix={suffix} prefix={prefix} />
      </p>
      {change !== undefined && (
        <div className="mt-2.5 flex items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold',
              positive ? 'bg-success-500/20 text-success-500' : 'bg-danger-500/20 text-danger-500',
            )}
          >
            {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(change)}%
          </span>
          <span className="text-xs font-medium text-canvas-fg/50">{changeLabel}</span>
        </div>
      )}
    </motion.div>
  )
}
